import { Trans } from "@lingui/macro";
import { ApiEpBilling } from "@smp/api";
import {
  UiAlert,
  UiBox,
  UiButton,
  UiDialogResponsive,
  UiGrid,
  UiIconBoost,
  UiLink,
  UiTypography,
} from "@smp/ui";
import { useQuery } from "react-query";

import { BlockTryPro } from "@/components/blocks/block_try_pro";
import { useDialog } from "@/hooks/use_dialog";
import { useEpBillingAccount } from "@/hooks/use_ep_billing_account";
import { DialogPlanBoost } from "@/screens/app/components/dialogs/dialog_plan_boost";

import { useStyles } from "./dialog_classic.css";
import { DialogGetPosts } from "./dialog_get_posts";

export const DialogClassic = ({ onClose }: { onClose: () => void }) => {
  const {
    close: dialogPlanBoostClose,
    isOpen: isDialogPlanBoostOpen,
    open: dialogPlanBoostOpen,
  } = useDialog();
  const classes = useStyles();
  const {
    close: dialogGetPostsClose,
    isOpen: isDialogGetPostsOpen,
    open: dialogGetPostsOpen,
  } = useDialog();
  const { accountList } = useEpBillingAccount();

  const { data: dataProductsPostPackage } = useQuery(
    [
      "ep.billing.product.posts_package",
      {
        query: { is_single_payment_allowed: true },
      },
    ],
    () =>
      ApiEpBilling.Product.PostsPackage.list({
        query: { is_single_payment_allowed: true },
      }),
  );

  if (!accountList || !dataProductsPostPackage) return null;

  const [postsAmountFree, postsAmountPaid] = accountList.reduce(
    (accumulator, account) => {
      if (account.commodity === "POST" && account.name === "free_posts") {
        accumulator[0] += account.balance;
      }
      if (account.commodity === "POST" && account.name === "posts") {
        accumulator[1] += account.balance;
      }
      return accumulator;
    },
    [0, 0],
  );

  return (
    <>
      <UiDialogResponsive
        fullWidth
        maxWidth="sm"
        onClose={onClose}
        open
        title={<Trans>Posts</Trans>}
      >
        <UiGrid container spacing={2}>
          <UiGrid item xs={12} sm={8}>
            <UiAlert variant="info">
              <>
                <UiTypography>
                  <Trans>Free posts are used first.</Trans>
                </UiTypography>
                <UiTypography>
                  <Trans>You can get 100 posts every month.</Trans>
                </UiTypography>
                <UiTypography gutterBottom>
                  <Trans>Free posts are not accumulated.</Trans>
                </UiTypography>
                <UiLink onClick={dialogGetPostsOpen}>
                  <Trans>Get more posts</Trans>
                </UiLink>
              </>
            </UiAlert>
          </UiGrid>
          <UiGrid item xs={12} sm={4}>
            <UiBox display="flex" flexDirection="column" height="100%">
              <UiBox flexGrow={1} />
              <UiBox
                alignItems="center"
                display="flex"
                flexDirection="row"
                height={48}
                paddingRight={1}
              >
                <UiBox flexGrow={1}>
                  <UiTypography>
                    <Trans>Paid posts</Trans>
                  </UiTypography>
                </UiBox>
                <UiBox flexGrow={1}>
                  <UiTypography align="right">{postsAmountPaid}</UiTypography>
                </UiBox>
              </UiBox>
              <UiBox
                alignItems="center"
                borderBottom="solid 1px #EEE"
                borderTop="solid 1px #EEE"
                display="flex"
                flexDirection="row"
                height={48}
                paddingRight={1}
              >
                <UiBox flexGrow={1}>
                  <UiTypography>
                    <Trans>Free posts</Trans>
                  </UiTypography>
                </UiBox>
                <UiBox flexGrow={1}>
                  <UiTypography align="right">{postsAmountFree}</UiTypography>
                </UiBox>
              </UiBox>
              <UiBox
                alignItems="center"
                display="flex"
                flexDirection="row"
                height={48}
                paddingRight={1}
              >
                <UiBox flexGrow={1}>
                  <UiTypography>
                    <Trans>Total posts</Trans>
                  </UiTypography>
                </UiBox>
                <UiBox flexGrow={1}>
                  <UiTypography align="right">
                    {postsAmountFree + postsAmountPaid}
                  </UiTypography>
                </UiBox>
              </UiBox>
              <UiBox flexGrow={1} />
            </UiBox>
          </UiGrid>
          <UiGrid item xs={12}>
            <UiButton
              align="left"
              color="paper"
              className={classes.button_boost}
              fullWidth
              onClick={dialogPlanBoostOpen}
              startIcon={<UiIconBoost />}
              variant="outlined"
            >
              <Trans>Power up my Plan</Trans>
            </UiButton>
          </UiGrid>
          <UiGrid item xs={12}>
            <BlockTryPro additionalAction={onClose} background />
          </UiGrid>
        </UiGrid>
      </UiDialogResponsive>

      {isDialogPlanBoostOpen && (
        <DialogPlanBoost
          onClose={dialogPlanBoostClose}
          onClickTryPro={onClose}
        />
      )}

      {isDialogGetPostsOpen && (
        <DialogGetPosts
          open={isDialogGetPostsOpen}
          onClose={dialogGetPostsClose}
        />
      )}
    </>
  );
};
